import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Flag, Plus } from "@fleet/icons";
import { Card, CardContent, Label, Skeleton, Switch, TextField } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@journal/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type FeatureFlag = components["schemas"]["FeatureFlag"];
type FlagListResponse = components["schemas"]["FeatureFlagListResponse"];
type UpsertFlag = components["schemas"]["UpsertFeatureFlagDto"];

export function FlagsManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const canWrite = () => can("flags.write");

  const [createOpen, setCreateOpen] = createSignal(false);
  const [newKey, setNewKey] = createSignal("");
  const [newDescription, setNewDescription] = createSignal("");
  const [newEnabled, setNewEnabled] = createSignal(true);

  const flagsQuery = createApiQuery<FlagListResponse, ErrorModel>(
    () => adminQueryKeys.flags(),
    async () => api.GET("/api/admin/flags", {}),
    () => ({ staleTime: 15_000 }),
  );

  const flags = createMemo<FeatureFlag[]>(() => flagsQuery.data?.data ?? []);

  const upsertMutation = createApiMutation(async (vars: UpsertFlag) =>
    api.POST("/api/admin/flags", { body: vars }),
  );

  const refetch = () => {
    void flagsQuery.refetch();
  };

  const toggleFlag = (flag: FeatureFlag, enabled: boolean) => {
    upsertMutation.mutate(
      { key: flag.key, description: flag.description, enabled, rollout: flag.rollout },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Flag updated`), "success");
        },
      },
    );
  };

  const createFlag = () => {
    const key = newKey().trim();
    if (!key) {
      return;
    }
    upsertMutation.mutate(
      { key, description: newDescription().trim() || undefined, enabled: newEnabled() },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Flag "${key}" saved`), "success");
          setCreateOpen(false);
          setNewKey("");
          setNewDescription("");
          setNewEnabled(true);
        },
      },
    );
  };

  return (
    <div class="space-y-6">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <Flag class="h-8 w-8 text-primary" />
          <div>
            <h2 class="text-3xl font-bold tracking-tight">{t(msg`Feature Flags`)}</h2>
            <p class="text-sm text-muted-foreground">
              {t(msg`Toggle and roll out features at runtime.`)}
            </p>
          </div>
        </div>
        <Show when={canWrite()}>
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus class="mr-2 h-4 w-4" />
            {t(msg`New flag`)}
          </Button>
        </Show>
      </div>

      {/* P23/CC-60 · I3: «no hay respuesta» ≠ «no hay nada». Sin este ramal
          un fallo de red se pintaba como una lista vacía. */}
      <Show when={flagsQuery.isError}>
        <QueryErrorState
          testId="admin-flags-error"
          compact
          onRetry={() => void flagsQuery.refetch()}
        />
      </Show>
      <Show when={!flagsQuery.isError}>
        <Show
          when={!flagsQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 5 }, () => 0)}>
                {() => <Skeleton class="h-16 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For
              each={flags()}
              fallback={
                <Card>
                  <CardContent class="p-8 text-center text-sm text-muted-foreground">
                    {t(msg`No feature flags yet.`)}
                  </CardContent>
                </Card>
              }
            >
              {(flag) => (
                <Card>
                  <CardContent class="p-4 flex items-center justify-between gap-4">
                    <div class="min-w-0">
                      <div class="font-mono font-bold truncate">{flag.key}</div>
                      <Show when={flag.description}>
                        <div class="text-xs text-muted-foreground truncate">{flag.description}</div>
                      </Show>
                    </div>
                    <Switch
                      checked={flag.enabled}
                      disabled={!canWrite() || upsertMutation.isPending}
                      onCheckedChange={(enabled) => {
                        toggleFlag(flag, enabled);
                      }}
                    />
                  </CardContent>
                </Card>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <Dialog open={createOpen()} onOpenChange={setCreateOpen} contentClass="max-w-[460px] p-6">
        <DialogTitle class="text-xl font-bold mb-2">{t(msg`New feature flag`)}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-4">
          {t(msg`Upsert by key. Existing keys are updated.`)}
        </DialogDescription>
        <div class="space-y-4">
          <div class="space-y-2">
            <Label for="flag-key">{t(msg`Key`)}</Label>
            <TextField
              id="flag-key"
              placeholder={t(msg`new_dashboard`)}
              value={newKey()}
              onInput={(e) => {
                setNewKey(e.currentTarget.value);
              }}
            />
          </div>
          <div class="space-y-2">
            <Label for="flag-desc">{t(msg`Description`)}</Label>
            <TextField
              id="flag-desc"
              value={newDescription()}
              onInput={(e) => {
                setNewDescription(e.currentTarget.value);
              }}
            />
          </div>
          <div class="flex items-center justify-between">
            <Label for="flag-enabled">{t(msg`Enabled`)}</Label>
            <Switch checked={newEnabled()} onCheckedChange={setNewEnabled} />
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <Button
            variant="ghost"
            onClick={() => {
              setCreateOpen(false);
            }}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button disabled={upsertMutation.isPending || !newKey().trim()} onClick={createFlag}>
            {t(msg`Save`)}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
